"use client";

import { useState, useEffect } from "react";
import { apiConfig } from "../config/api.config";
import CDPViewer from "./CDPViewer";
import RPViewer from "./RPViewer";

export default function PresupuestoSolicitudesModal({
  show,
  onClose,
  contrato,
  userRole,
}) {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormularioViewer, setShowFormularioViewer] = useState(false);
  const [currentFormularioData, setCurrentFormularioData] = useState(null);
  const [currentFormularioType, setCurrentFormularioType] = useState(null);
  const [currentSolicitud, setCurrentSolicitud] = useState(null);

  const contractId = contrato?.id;
  const contractData = contrato;

  useEffect(() => {
    if (!show || !contractId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contractId]);

  const resolveFileUrl = (rawUrl) => {
    if (!rawUrl) return "";
    let u = String(rawUrl).trim();
    if (!u) return "";

    if (/^https?:\/\//i.test(u)) {
      if (u.includes("/uploads/") && !u.includes("/api/uploads/")) {
        u = u.replace("/uploads/", "/api/uploads/");
      }
      return u;
    }

    if (!u.startsWith("/")) u = "/" + u;

    if (u.startsWith("/uploads/")) {
      u = "/api" + u;
    }

    if (u.startsWith("/api/")) {
      return apiConfig.getUrl(u);
    }

    return apiConfig.getUrl(u);
  };

  const handleDownload = async (url, name) => {
    try {
      const downloadUrl = resolveFileUrl(url);

      if (!downloadUrl) {
        throw new Error("URL inválida para descarga.");
      }

      const response = await fetch(downloadUrl, {
        credentials: "include",
        redirect: "follow",
        headers: {
          Accept: "application/pdf,application/octet-stream;q=0.9,*/*;q=0.8",
        },
      });

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}. ${txt.slice(0, 180)}`);
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();

      if (contentType.includes("text/html")) {
        const txt = await response.text().catch(() => "");
        throw new Error(
          `El servidor devolvió HTML (probable login/redirect/404). Muestra: ${txt.slice(0, 140)}`
        );
      }
      if (contentType.includes("application/json")) {
        const txt = await response.text().catch(() => "");
        throw new Error(
          `El servidor devolvió JSON (error del API). Muestra: ${txt.slice(0, 180)}`
        );
      }

      const blob = await response.blob();

      if (!blob || blob.size < 50) {
        throw new Error("Archivo vacío o demasiado pequeño.");
      }

      const head = await blob.slice(0, 5).text().catch(() => "");
      const looksLikePdf = head === "%PDF-";

      if (!looksLikePdf) {
        const peek = await blob.slice(0, 120).text().catch(() => "");
        throw new Error(
          `Lo descargado no parece PDF. Firma: "${head}". Muestra: ${peek.slice(0, 120)}`
        );
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      let safeName = (name && String(name).trim()) || "archivo.pdf";
      if (!safeName.toLowerCase().endsWith(".pdf")) safeName += ".pdf";
      link.download = safeName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("❌ Error al descargar:", err);
      alert(`Error al descargar.\n\n${err?.message || "Desconocido"}`);
    }
  };

  const loadData = async () => {
    console.log("🚀 Cargando CDP/RP/Almacén/Inexistencia para:", contractId);
    setLoading(true);
    setError(null);

    try {
      // ── CDP / RP ──────────────────────────────────────────────────────────
      const urlPres = apiConfig.getUrl(
        "/api/presupuesto/solicitudes/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );

      const resPres = await fetch(urlPres, { credentials: "include" });
      if (!resPres.ok) throw new Error(`HTTP ${resPres.status}`);

      const json = await resPres.json();
      let presRequests = json.solicitudes || json.data || json || [];

      const urlCdp = apiConfig.getUrl(
        "/api/cdp/formularios/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );
      const urlRp = apiConfig.getUrl(
        "/api/rp/formularios/index.php",
        `contract_id=${encodeURIComponent(contractId)}`
      );

      const [resCdp, resRp] = await Promise.all([
        fetch(urlCdp, { credentials: "include" }).catch(() => null),
        fetch(urlRp, { credentials: "include" }).catch(() => null),
      ]);

      let cdpForms = [];
      let rpForms = [];

      if (resCdp?.ok) {
        const dataCdp = await resCdp.json();
        cdpForms = Array.isArray(dataCdp)
          ? dataCdp
          : dataCdp.formularios || dataCdp.data || dataCdp.rows || [];
      }

      if (resRp?.ok) {
        const dataRp = await resRp.json();
        rpForms = Array.isArray(dataRp)
          ? dataRp
          : dataRp.formularios || dataRp.data || dataRp.rows || [];
      }

      const solicitudesConFormularios = presRequests.map((r) => {
        const tipo = String(r.request_type || "").toUpperCase();
        let formulario = null;

        if (tipo === "CDP" && cdpForms.length > 0) formulario = cdpForms[0];
        else if (tipo === "RP" && rpForms.length > 0) formulario = rpForms[0];

        return { ...r, request_type: tipo, formulario };
      });

      // ── ALMACÉN ───────────────────────────────────────────────────────────
      let solicitudesAlmacen = [];
      try {
        const almacenEndpoint =
          apiConfig?.endpoints?.almacenSolicitudes ||
          "/api/almacen/solicitudes/index.php";

        const urlAlm = apiConfig.getUrl(
          almacenEndpoint,
          `contract_id=${encodeURIComponent(contractId)}`
        );

        const resAlm = await fetch(urlAlm, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (resAlm?.ok) {
          const jsonAlm = await resAlm.json();

          const arr = Array.isArray(jsonAlm)
            ? jsonAlm
            : jsonAlm?.data || jsonAlm?.rows || [];

          solicitudesAlmacen = (Array.isArray(arr) ? arr : []).map((r) => ({
            ...r,
            request_type: "ALMACÉN",
            sent_at: r.sent_at || r.created_at || r.fecha || null,
          }));
        }
      } catch (err) {
        console.warn("No se pudieron cargar solicitudes de almacén:", err);
      }

      // ── INEXISTENCIA ──────────────────────────────────────────────────────
      // Usa by-contract/index.php que acepta ?contract_id= y devuelve {ok, rows}
      let solicitudesInexistencia = [];
      try {
        const urlInex = apiConfig.getUrl(
          "/api/inexistencia/by-contract/index.php",
          `contract_id=${encodeURIComponent(contractId)}`
        );

        const resInex = await fetch(urlInex, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (resInex?.ok) {
          const jsonInex = await resInex.json();

          // by-contract devuelve { ok: true, rows: [...] }
          const arr = Array.isArray(jsonInex)
            ? jsonInex
            : jsonInex?.rows || jsonInex?.data || [];

          solicitudesInexistencia = (Array.isArray(arr) ? arr : []).map((r) => {
            // Normalizar status al mismo vocabulario que CDP/RP/Almacén
            const rawStatus = String(r.status || "").toLowerCase();
            let response_status = "pending";
            if (rawStatus === "approved" || rawStatus === "finalizado" || rawStatus === "aprobado") {
              response_status = "aprobado";
            } else if (rawStatus === "rechazado" || rawStatus === "rejected" || rawStatus === "negado") {
              response_status = "negado";
            }

            return {
              ...r,
              request_type: "INEXISTENCIA",
              sent_at: r.sent_at || r.created_at || null,
              response_status,
              // motivo de rechazo viene en 'notes' según el PHP
              motivo_rechazo: r.notes || null,
              // PDF final lo sube el rol 10 al aprobar
              pdf_url: r.pdf_url || null,
              // objeto del contrato
              objeto_snapshot: r.objeto_snapshot || null,
            };
          });
        }
      } catch (err) {
        console.warn("No se pudieron cargar solicitudes de inexistencia:", err);
      }

      // ── Combinar y ordenar ────────────────────────────────────────────────
      const todasSolicitudes = [
        ...solicitudesConFormularios,
        ...solicitudesAlmacen,
        ...solicitudesInexistencia,
      ];

      todasSolicitudes.sort((a, b) => {
        const dateA = new Date(a.sent_at || 0).getTime();
        const dateB = new Date(b.sent_at || 0).getTime();
        return dateB - dateA;
      });

      setSolicitudes(todasSolicitudes);
    } catch (err) {
      console.error("❌ Error:", err);
      setError("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString("es-ES");
    } catch (e) {
      return "N/A";
    }
  };

  const extractAttachments = (attachmentsData) => {
    if (!attachmentsData) return [];

    try {
      const parsed =
        typeof attachmentsData === "string"
          ? JSON.parse(attachmentsData)
          : attachmentsData;

      if (Array.isArray(parsed)) return parsed;
      else if (typeof parsed === "object" && parsed)
        return Object.values(parsed);
    } catch (e) {
      console.error("Error parsing attachments:", e);
    }
    return [];
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          width: "95%",
          maxWidth: "700px",
          maxHeight: "80vh",
          borderRadius: "0.5rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            backgroundColor: "#0e7490",
            color: "white",
            padding: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>
              Solicitudes CDP/RP/Almacén/Inexistencia — {contractData?.nombre_contratista}
            </h3>
            <button
              onClick={loadData}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.3)",
                color: "white",
                padding: "0.25rem 0.5rem",
                borderRadius: "0.25rem",
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              🔄
            </button>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "1.5rem",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "1rem", overflowY: "auto", flex: 1 }}>
          {loading ? (
            <p style={{ textAlign: "center", color: "#666" }}>Cargando...</p>
          ) : error ? (
            <p style={{ textAlign: "center", color: "#dc2626" }}>{error}</p>
          ) : solicitudes.length === 0 ? (
            <p style={{ textAlign: "center", color: "#666" }}>
              No hay solicitudes.
            </p>
          ) : (
            solicitudes.map((req, idx) => {
              const tipo = req.request_type;
              const status = req.response_status || req.status || "pending";

              // ── Colores por tipo ──────────────────────────────────────────
              const isAlmacen     = tipo === "ALMACÉN";
              const isInexistencia = tipo === "INEXISTENCIA";

              const cardBgColor = isInexistencia
                ? "#F5F3FF"
                : isAlmacen
                ? "#DBEAFE"
                : "#f9fafb";

              const cardBorderColor = isInexistencia
                ? "#7C3AED"
                : isAlmacen
                ? "#3B82F6"
                : "#e5e7eb";

              // Badge de estado
              const bgColor =
                status === "aprobado"
                  ? "#dcfce7"
                  : status === "negado"
                  ? "#fee2e2"
                  : "#fef9c3";
              const textColor =
                status === "aprobado"
                  ? "#166534"
                  : status === "negado"
                  ? "#b91c1c"
                  : "#854d0e";

              const attachments         = extractAttachments(req.attachments);
              const responseAttachments = extractAttachments(req.response_attachments);

              // Badge del tipo (color pill)
              const tipoBadgeBg = isInexistencia
                ? "#7C3AED"
                : isAlmacen
                ? "#3B82F6"
                : tipo === "CDP"
                ? "#FACC15"
                : "#22C55E";

              const tipoBadgeColor = isInexistencia
                ? "#FFFFFF"
                : isAlmacen
                ? "#FFFFFF"
                : tipo === "CDP"
                ? "#854D0E"
                : "#166534";

              return (
                <div
                  key={req.id || idx}
                  style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    border: `2px solid ${cardBorderColor}`,
                    borderRadius: "0.5rem",
                    backgroundColor: cardBgColor,
                  }}
                >
                  {/* ── Cabecera de la tarjeta ── */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.75rem",
                    }}
                  >
                    <div>
                      <strong>Solicitud</strong>
                      {tipo && (
                        <span
                          style={{
                            marginLeft: "0.5rem",
                            padding: "0.25rem 0.6rem",
                            borderRadius: "9999px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            backgroundColor: tipoBadgeBg,
                            color: tipoBadgeColor,
                            border: `1px solid ${cardBorderColor}`,
                          }}
                        >
                          {tipo}
                        </span>
                      )}
                      <span
                        style={{
                          marginLeft: "0.5rem",
                          color: "#6b7280",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formatDate(req.sent_at)}
                      </span>
                      <span
                        style={{
                          marginLeft: "0.75rem",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor: bgColor,
                          color: textColor,
                        }}
                      >
                        {status === "aprobado"
                          ? "Aprobado"
                          : status === "negado"
                          ? "Negado"
                          : "Pendiente"}
                      </span>
                    </div>
                  </div>

                  {/* ── Objeto (solo Inexistencia) ── */}
                  {isInexistencia && req.objeto_snapshot && (
                    <div
                      style={{
                        marginBottom: "0.75rem",
                        fontSize: "0.875rem",
                        color: "#374151",
                      }}
                    >
                      <strong>Objeto:</strong>{" "}
                      <span style={{ fontStyle: "italic" }}>{req.objeto_snapshot}</span>
                    </div>
                  )}

                  {/* ── Documentos enviados (CDP/RP/Almacén) ── */}
                  {attachments.length > 0 && (
                    <div style={{ marginBottom: "0.75rem" }}>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: "0.5rem",
                        }}
                      >
                        📤 Documentos enviados:
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                        {attachments.map((file, i) => {
                          const url  = typeof file === "string" ? file : file?.url;
                          const name = typeof file === "object" && file?.name
                            ? file.name
                            : `Archivo ${i + 1}`;
                          return url ? (
                            <button
                              key={i}
                              type="button"
                              onClick={() => handleDownload(url, name)}
                              style={{
                                padding: "0.5rem 1rem",
                                backgroundColor: "#3b82f6",
                                color: "white",
                                borderRadius: "0.375rem",
                                fontSize: "0.875rem",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              📄 {name}
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Botón Ver Formulario CDP/RP ── */}
                  {req.formulario && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentSolicitud(req);
                        setCurrentFormularioData(req.formulario);
                        setCurrentFormularioType(tipo);
                        setShowFormularioViewer(true);
                      }}
                      style={{
                        backgroundColor: "#0ea5e9",
                        color: "white",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Ver Formulario {tipo}
                    </button>
                  )}

                  {/* ── Botón Ver Documentos (Almacén) ── */}
                  {isAlmacen && (attachments.length > 0 || responseAttachments.length > 0) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentSolicitud(req);
                        setCurrentFormularioData(null);
                        setCurrentFormularioType("ALMACÉN");
                        setShowFormularioViewer(true);
                      }}
                      style={{
                        backgroundColor: "#3B82F6",
                        color: "white",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Ver Documentos
                    </button>
                  )}

                  {/* ── Botón Descargar Certificado (Inexistencia aprobada con PDF) ── */}
                  {isInexistencia && status === "aprobado" && req.pdf_url && (
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(
                          req.pdf_url,
                          `certificado_inexistencia_${req.id || idx}.pdf`
                        )
                      }
                      style={{
                        backgroundColor: "#7C3AED",
                        color: "white",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        marginBottom: "0.75rem",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.4rem",
                      }}
                    >
                      📄 Descargar Certificado
                    </button>
                  )}

                  {/* ── Bloque de estado ── */}
                  <div
                    style={{
                      padding: "0.75rem",
                      backgroundColor: bgColor,
                      borderRadius: "0.375rem",
                    }}
                  >
                    <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.875rem" }}>
                      <strong>Estado:</strong>
                      <span style={{ marginLeft: "0.5rem", color: textColor }}>
                        {status === "aprobado"
                          ? "✅ Aprobado"
                          : status === "negado"
                          ? "❌ Negado"
                          : "⏳ Pendiente"}
                      </span>
                    </p>

                    {/* Fase actual (solo Inexistencia pendiente) */}
                    {isInexistencia && status === "pending" && (
                      <p
                        style={{
                          margin: "0.25rem 0",
                          fontSize: "0.875rem",
                          color: "#6b7280",
                        }}
                      >
                        Fase:{" "}
                        {Number(req.current_step) === 9
                          ? "Pendiente firma Validador"
                          : Number(req.current_step) === 10
                          ? "Pendiente firma Secretario"
                          : "En proceso"}
                      </p>
                    )}

                    {req.responded_at && (
                      <p
                        style={{
                          margin: "0.25rem 0",
                          fontSize: "0.875rem",
                          color: "#6b7280",
                        }}
                      >
                        Respondido: {formatDate(req.responded_at)}
                      </p>
                    )}

                    {req.fecha_documento && (
                      <p
                        style={{
                          margin: "0.25rem 0",
                          fontSize: "0.875rem",
                          color: "#6b7280",
                        }}
                      >
                        Fecha documento: {formatDate(req.fecha_documento)}
                      </p>
                    )}

                    {req.numero_documento && (
                      <p
                        style={{
                          margin: "0.25rem 0",
                          fontSize: "0.875rem",
                          color: "#6b7280",
                        }}
                      >
                        N° documento: {req.numero_documento}
                      </p>
                    )}

                    {req.nuevo_resumido && (
                      <div
                        style={{
                          marginTop: "0.75rem",
                          padding: "0.75rem",
                          backgroundColor: "rgba(255,255,255,0.5)",
                          borderRadius: "0.375rem",
                        }}
                      >
                        <strong style={{ fontSize: "0.875rem" }}>
                          Nuevo resumido:
                        </strong>
                        <p
                          style={{
                            margin: "0.25rem 0 0 0",
                            fontSize: "0.875rem",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {req.nuevo_resumido}
                        </p>
                        {req.nombre_nuevo_resumido && (
                          <p
                            style={{
                              margin: "0.5rem 0 0 0",
                              fontSize: "0.75rem",
                              fontStyle: "italic",
                              color: "#6b7280",
                            }}
                          >
                            ({req.nombre_nuevo_resumido})
                          </p>
                        )}
                      </div>
                    )}

                    {/* Documentos de respuesta */}
                    {responseAttachments.length > 0 && (
                      <div style={{ marginTop: "0.75rem" }}>
                        <div
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            marginBottom: "0.5rem",
                            color: "#166534",
                          }}
                        >
                          📥 Documentos de respuesta:
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.5rem",
                          }}
                        >
                          {responseAttachments.map((file, i) => {
                            const url  = typeof file === "string" ? file : file?.url;
                            const name = typeof file === "object" && file?.name
                              ? file.name
                              : `Respuesta ${i + 1}`;
                            return url ? (
                              <button
                                key={i}
                                type="button"
                                onClick={() => handleDownload(url, name)}
                                style={{
                                  padding: "0.5rem 1rem",
                                  backgroundColor: "#10b981",
                                  color: "white",
                                  borderRadius: "0.375rem",
                                  fontSize: "0.875rem",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                📄 {name}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Motivo de rechazo — aplica a TODOS los tipos incluido INEXISTENCIA */}
                    {["negado", "rechazado", "denegado"].includes(
                      String(status).toLowerCase()
                    ) &&
                      (req?.motivoRechazo ??
                        req?.motivo_rechazo ??
                        req?.observaciones ??
                        req?.motivo ??
                        req?.notes) && (
                        <div
                          style={{
                            marginTop: "0.75rem",
                            padding: "0.75rem",
                            backgroundColor: "#fef2f2",
                            border: "1px solid #fecaca",
                            borderRadius: "0.375rem",
                          }}
                        >
                          <strong style={{ color: "#dc2626", fontSize: "0.875rem" }}>
                            Motivo del rechazo:
                          </strong>
                          <p
                            style={{
                              margin: "0.25rem 0 0 0",
                              fontSize: "0.875rem",
                              fontStyle: "italic",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {req?.motivoRechazo ??
                              req?.motivo_rechazo ??
                              req?.observaciones ??
                              req?.motivo ??
                              req?.notes}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          MODAL VISOR FORMULARIO / DOCUMENTOS
         ═══════════════════════════════════════════ */}
      {showFormularioViewer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              width: "90%",
              maxWidth: "800px",
              height: "90%",
              borderRadius: "0.5rem",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                backgroundColor: "#0e7490",
                color: "white",
                padding: "1rem",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ margin: 0 }}>
                {currentFormularioType === "ALMACÉN"
                  ? "Solicitud de Almacén"
                  : currentFormularioType === "INEXISTENCIA"
                  ? "Certificación de Inexistencia"
                  : `Formulario ${currentFormularioType}`}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowFormularioViewer(false);
                  setCurrentSolicitud(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "1rem" }}>
              {/* ── CDP ── */}
              {currentFormularioType === "CDP" && currentFormularioData && (
                <CDPViewer cdpData={currentFormularioData} />
              )}

              {/* ── RP ── */}
              {currentFormularioType === "RP" && currentFormularioData && (
                <RPViewer rpData={currentFormularioData} />
              )}

              {/* ── ALMACÉN ── */}
              {currentFormularioType === "ALMACÉN" && currentSolicitud && (
                <div style={{ marginBottom: "2rem" }}>
                  <h4 style={{ marginTop: 0, color: "#1f2937" }}>
                    Información de la Solicitud
                  </h4>
                  <div
                    style={{
                      padding: "1rem",
                      backgroundColor: "#f9fafb",
                      borderRadius: "0.5rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <p style={{ margin: "0.5rem 0" }}>
                      <strong>Fecha:</strong>{" "}
                      {formatDate(currentSolicitud.sent_at)}
                    </p>
                    {currentSolicitud.observaciones && (
                      <p style={{ margin: "0.5rem 0" }}>
                        <strong>Observaciones:</strong>{" "}
                        {currentSolicitud.observaciones}
                      </p>
                    )}
                    {currentSolicitud.numero_documento && (
                      <p style={{ margin: "0.5rem 0" }}>
                        <strong>N° Documento:</strong>{" "}
                        {currentSolicitud.numero_documento}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── INEXISTENCIA ── */}
              {currentFormularioType === "INEXISTENCIA" && currentSolicitud && (
                <div style={{ marginBottom: "2rem" }}>
                  <h4 style={{ marginTop: 0, color: "#1f2937" }}>
                    Información de la Solicitud de Inexistencia
                  </h4>
                  <div
                    style={{
                      padding: "1rem",
                      backgroundColor: "#F5F3FF",
                      border: "1px solid #DDD6FE",
                      borderRadius: "0.5rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <p style={{ margin: "0.5rem 0" }}>
                      <strong>Fecha:</strong>{" "}
                      {formatDate(currentSolicitud.sent_at)}
                    </p>
                    <p style={{ margin: "0.5rem 0" }}>
                      <strong>Estado:</strong>{" "}
                      {currentSolicitud.response_status === "aprobado"
                        ? "✅ Aprobado (Certificado emitido)"
                        : currentSolicitud.response_status === "negado"
                        ? "❌ Rechazado"
                        : Number(currentSolicitud.current_step) === 9
                        ? "⏳ Pendiente firma Validador"
                        : Number(currentSolicitud.current_step) === 10
                        ? "⏳ Pendiente firma Secretario"
                        : "⏳ En proceso"}
                    </p>
                    {currentSolicitud.objeto_snapshot && (
                      <p style={{ margin: "0.5rem 0" }}>
                        <strong>Objeto:</strong>{" "}
                        <span style={{ fontStyle: "italic" }}>
                          {currentSolicitud.objeto_snapshot}
                        </span>
                      </p>
                    )}
                    {/* Motivo de rechazo dentro del visor */}
                    {currentSolicitud.response_status === "negado" &&
                      currentSolicitud.notes && (
                        <div
                          style={{
                            marginTop: "0.75rem",
                            padding: "0.75rem",
                            backgroundColor: "#fef2f2",
                            border: "1px solid #fecaca",
                            borderRadius: "0.375rem",
                          }}
                        >
                          <strong style={{ color: "#dc2626", fontSize: "0.875rem" }}>
                            Motivo del rechazo:
                          </strong>
                          <p
                            style={{
                              margin: "0.25rem 0 0 0",
                              fontSize: "0.875rem",
                              fontStyle: "italic",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {currentSolicitud.notes}
                          </p>
                        </div>
                      )}
                  </div>

                  {/* Botón descarga PDF dentro del visor */}
                  {currentSolicitud.response_status === "aprobado" &&
                    currentSolicitud.pdf_url && (
                      <button
                        type="button"
                        onClick={() =>
                          handleDownload(
                            currentSolicitud.pdf_url,
                            `certificado_inexistencia_${currentSolicitud.id || ""}.pdf`
                          )
                        }
                        style={{
                          backgroundColor: "#7C3AED",
                          color: "white",
                          padding: "0.6rem 1.2rem",
                          borderRadius: "0.375rem",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          marginBottom: "1rem",
                        }}
                      >
                        📄 Descargar Certificado PDF
                      </button>
                    )}
                </div>
              )}

              {/* ── Documentos adjuntos (Almacén e Inexistencia comparten este bloque) ── */}
              {currentSolicitud &&
                (currentFormularioType === "ALMACÉN" ||
                  currentFormularioType === "INEXISTENCIA") &&
                (() => {
                  const attachments = extractAttachments(
                    currentSolicitud.attachments
                  );
                  const responseAttachments = extractAttachments(
                    currentSolicitud.response_attachments
                  );

                  if (
                    attachments.length === 0 &&
                    responseAttachments.length === 0
                  ) {
                    return (
                      <div
                        style={{
                          padding: "2rem",
                          textAlign: "center",
                          color: "#6b7280",
                          backgroundColor: "#f9fafb",
                          borderRadius: "0.5rem",
                        }}
                      >
                        No hay documentos adjuntos
                      </div>
                    );
                  }

                  return (
                    <div>
                      {attachments.length > 0 && (
                        <div style={{ marginBottom: "1.5rem" }}>
                          <div
                            style={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              marginBottom: "0.75rem",
                              color: "#1f2937",
                            }}
                          >
                            📤 Documentos Enviados:
                          </div>
                          <div
                            style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
                          >
                            {attachments.map((file, i) => {
                              const url =
                                typeof file === "string" ? file : file?.url;
                              const name =
                                typeof file === "object" && file?.name
                                  ? file.name
                                  : `Archivo ${i + 1}`;
                              return url ? (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => handleDownload(url, name)}
                                  style={{
                                    padding: "0.6rem 1.2rem",
                                    backgroundColor: "#3b82f6",
                                    color: "white",
                                    borderRadius: "0.375rem",
                                    fontSize: "0.875rem",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  📄 {name}
                                </button>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}

                      {responseAttachments.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              marginBottom: "0.75rem",
                              color: "#166534",
                            }}
                          >
                            📥 Documentos de Respuesta
                            {currentFormularioType === "ALMACÉN"
                              ? " (Certificados)"
                              : ""}
                            :
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "0.5rem",
                            }}
                          >
                            {responseAttachments.map((file, i) => {
                              const url =
                                typeof file === "string" ? file : file?.url;
                              const name =
                                typeof file === "object" && file?.name
                                  ? file.name
                                  : currentFormularioType === "ALMACÉN"
                                  ? `Certificado ${i + 1}`
                                  : `Respuesta ${i + 1}`;
                              return url ? (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => handleDownload(url, name)}
                                  style={{
                                    padding: "0.6rem 1.2rem",
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    borderRadius: "0.375rem",
                                    fontSize: "0.875rem",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  📄 {name}
                                </button>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}